import { useEffect } from 'react';
import { useLang } from '../i18n/LanguageContext';

/**
 * Dependency-free SEO head manager.
 *
 * react-helmet-async is incompatible with React 19 (silently fails to write
 * to document.head), so this component manages title / meta / link / JSON-LD
 * tags imperatively. All managed nodes carry [data-glow-seo] and are
 * replaced wholesale whenever the route or language changes.
 *
 * Schema injected:
 *   - Organization (every page)
 *   - WebSite + SearchAction (homepage)
 *   - BreadcrumbList (auto-derived from url path, when 2+ segments)
 *   - Service (when service prop provided)
 *   - FAQPage (when faq prop provided)
 *   - Article (when type=article)
 */

const LANG_TO_HREFLANG = { EN: 'en', AR: 'ar', DE: 'de', ES: 'es' };
const META_LANG_LABEL = { EN: 'English', AR: 'Arabic', DE: 'German', ES: 'Spanish' };
const OG_LOCALE = { EN: 'en_US', AR: 'ar_SA', DE: 'de_DE', ES: 'es_ES' };

const titleCase = (slug) =>
  slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  author = 'GlowMeets',
  publishedTime,
  service, // { name, description, serviceType }
  faq, // [{ q, a }]
}) => {
  const { lang, t } = useLang();

  useEffect(() => {
    const siteUrl = process.env.REACT_APP_SITE_URL || 'https://glowmeets.com';
    const path = url || '';
    const fullUrl = `${siteUrl}${path}`;
    const defaultImage = `${siteUrl}/favicon-512.png`;
    const ogImage = image || defaultImage;
    const htmlLang = LANG_TO_HREFLANG[lang] || 'en';

    // Resolve title/description from i18n when the page hasn't passed one.
    const slugKey =
      !path || path === '/' ? 'home' : path.replace(/^\//, '').split('/')[0];
    const i18nTitle = t(`html.title.${slugKey}`);
    const i18nDescription = t(`html.description.${slugKey}`);
    const finalTitle =
      title ||
      (typeof i18nTitle === 'string' && i18nTitle !== `html.title.${slugKey}`
        ? i18nTitle
        : 'GlowMeets, Luxury MICE, Events & Destination Weddings in Türkiye');
    const finalDescription =
      description ||
      (typeof i18nDescription === 'string' &&
      i18nDescription !== `html.description.${slugKey}`
        ? i18nDescription
        : '');

    document.title = finalTitle;
    document.documentElement.setAttribute('lang', htmlLang);

    // Remove previously managed nodes, then write the fresh set.
    document.querySelectorAll('[data-glow-seo]').forEach((n) => n.remove());

    const fragment = document.createDocumentFragment();
    const addEl = (tag, attrs) => {
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      el.setAttribute('data-glow-seo', 'true');
      fragment.appendChild(el);
      return el;
    };
    const meta = (a) => addEl('meta', a);
    const link = (a) => addEl('link', a);
    const ldJson = (obj) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-glow-seo', 'true');
      s.textContent = JSON.stringify(obj);
      fragment.appendChild(s);
    };

    meta({ name: 'title', content: finalTitle });
    meta({ name: 'description', content: finalDescription });
    if (keywords) meta({ name: 'keywords', content: keywords });
    meta({ name: 'author', content: author });

    // Open Graph
    meta({ property: 'og:type', content: type });
    meta({ property: 'og:url', content: fullUrl });
    meta({ property: 'og:title', content: finalTitle });
    meta({ property: 'og:description', content: finalDescription });
    meta({ property: 'og:image', content: ogImage });
    meta({ property: 'og:image:alt', content: finalTitle });
    meta({ property: 'og:site_name', content: 'GlowMeets' });
    meta({ property: 'og:locale', content: OG_LOCALE[lang] || 'en_US' });
    Object.entries(OG_LOCALE)
      .filter(([code]) => code !== lang)
      .forEach(([, locale]) =>
        meta({ property: 'og:locale:alternate', content: locale })
      );
    if (type === 'article' && publishedTime) {
      meta({ property: 'article:published_time', content: publishedTime });
    }

    // Twitter
    meta({ property: 'twitter:card', content: 'summary_large_image' });
    meta({ property: 'twitter:url', content: fullUrl });
    meta({ property: 'twitter:title', content: finalTitle });
    meta({ property: 'twitter:description', content: finalDescription });
    meta({ property: 'twitter:image', content: ogImage });

    // Canonical + hreflang alternates with ?lang= variants (so Google can
    // resolve each language to a distinct URL even though the SPA serves
    // a single index.html for every route).
    link({ rel: 'canonical', href: fullUrl });
    [
      ['en', ''],
      ['ar', '?lang=ar'],
      ['de', '?lang=de'],
      ['es', '?lang=es'],
      ['x-default', ''],
    ].forEach(([hl, suffix]) =>
      link({
        rel: 'alternate',
        hreflang: hl,
        href: `${fullUrl}${suffix}`,
      })
    );

    meta({ name: 'robots', content: 'index, follow' });
    meta({ name: 'language', content: META_LANG_LABEL[lang] || 'English' });

    // Geo tags
    meta({ name: 'geo.region', content: 'TR' });
    meta({ name: 'geo.placename', content: 'Istanbul' });
    meta({ name: 'geo.position', content: '41.0082;28.9784' });
    meta({ name: 'ICBM', content: '41.0082, 28.9784' });

    // JSON-LD: Organization (every page)
    ldJson({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'GlowMeets',
      alternateName: 'GlowMeets by Mervin Travel',
      url: siteUrl,
      logo: `${siteUrl}/favicon-512.png`,
      image: `${siteUrl}/glowmeets-wordmark-mice.png`,
      description: finalDescription,
      sameAs: [],
      parentOrganization: {
        '@type': 'Organization',
        name: 'Mervin Travel',
        url: 'https://www.mervintravel.com',
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress:
          'Gursel Mahallesi Imrahor Caddesi No.29 B Premier Campus Plaza Suite 437',
        addressLocality: 'Istanbul',
        addressCountry: 'TR',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'mice@glowmeets.com',
        telephone: '+90-544-118-18-27',
        contactType: 'sales',
        availableLanguage: ['English', 'Arabic', 'German', 'Spanish', 'Turkish'],
      },
      areaServed: 'Türkiye',
      knowsAbout: [
        'MICE',
        'Corporate Events',
        'Destination Weddings',
        'Congress Management',
        'Incentive Travel',
        'Brand Activations',
      ],
    });

    // JSON-LD: WebSite + SearchAction (homepage only)
    if (path === '/' || path === '') {
      ldJson({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'GlowMeets',
        url: siteUrl,
        inLanguage: ['en', 'ar', 'de', 'es'],
        publisher: { '@type': 'Organization', name: 'GlowMeets' },
      });
    }

    // JSON-LD: BreadcrumbList (auto-derived from path)
    if (path && path !== '/') {
      const segments = path.replace(/^\//, '').split('/').filter(Boolean);
      const items = [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: `${siteUrl}/`,
        },
        ...segments.map((seg, i) => ({
          '@type': 'ListItem',
          position: i + 2,
          name: titleCase(seg),
          item: `${siteUrl}/${segments.slice(0, i + 1).join('/')}`,
        })),
      ];
      ldJson({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items,
      });
    }

    // JSON-LD: Service
    if (service) {
      ldJson({
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: service.name,
        serviceType: service.serviceType || service.name,
        description: service.description || finalDescription,
        provider: { '@type': 'Organization', name: 'GlowMeets', url: siteUrl },
        areaServed: { '@type': 'Country', name: 'Türkiye' },
        url: fullUrl,
      });
    }

    // JSON-LD: FAQPage
    if (Array.isArray(faq) && faq.length > 0) {
      ldJson({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((p) => ({
          '@type': 'Question',
          name: p.q,
          acceptedAnswer: { '@type': 'Answer', text: p.a },
        })),
      });
    }

    // JSON-LD: Article (journal pages)
    if (type === 'article') {
      ldJson({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: finalTitle,
        description: finalDescription,
        image: ogImage,
        url: fullUrl,
        inLanguage: htmlLang,
        ...(publishedTime ? { datePublished: publishedTime } : {}),
        author: { '@type': 'Organization', name: 'GlowMeets' },
        publisher: {
          '@type': 'Organization',
          name: 'GlowMeets',
          logo: { '@type': 'ImageObject', url: `${siteUrl}/favicon-512.png` },
        },
      });
    }

    document.head.appendChild(fragment);
  }, [
    lang,
    t,
    title,
    description,
    keywords,
    image,
    url,
    type,
    author,
    publishedTime,
    service,
    faq,
  ]);

  return null;
};

export default SEO;
